import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';
import { cn } from '@mdvip/shared/lib/utils';
import { createContext, useContext } from 'react';

/**
 * Two presentations, chosen on `<Tabs variant>`:
 *
 * - `toggle` (default): a card track holding rounded-full tabs; the active tab is a `--secondary`
 *   chip carrying bold weight. `--secondary` on card is 1.3:1 and the chip has no border, so weight
 *   is the only non-colour cue for the selected state. Quiet, for switching views that share one
 *   page heading.
 * - `pills`: separate outlined pills with a gap between them; the active pill takes a `--secondary`
 *   fill. At rest the border is `--border-control-soft`, which resolves to the same value as
 *   `--input`, so the border step alone does not read — the pill takes a hover fill instead,
 *   matching Button outline.
 *
 * `pill` and `boxed` are kept as aliases for the names these two carried before the DS renamed them.
 *
 * Class strings are ported from the design system. Active selectors read `data-active` rather than the
 * DS source's `data-[selected]`, which is just what the real Base UI primitive emits versus its
 * preview twin.
 */
const TAB_VARIANTS = {
  toggle: {
    list: 'flex items-center justify-center gap-1 rounded-full border border-border bg-card p-1.5 text-muted-foreground',
    trigger:
      'min-h-11 flex-1 rounded-full px-4 py-2 font-medium text-muted-foreground data-active:bg-secondary data-active:font-bold data-active:text-secondary-foreground data-active:shadow-sm',
  },
  pills: {
    list: 'flex w-full flex-col items-stretch justify-start gap-2 @min-[40rem]:flex-row @min-[40rem]:gap-4',
    trigger:
      'min-h-11 flex-1 rounded-[48px] border border-border-control-soft bg-card px-4 py-3 font-semibold text-foreground hover:border-input hover:bg-accent data-active:border-transparent data-active:bg-secondary data-active:text-secondary-foreground data-active:hover:bg-secondary',
  },
} as const;

/**
 * The classes every tab trigger carries, whatever the variant. Exported so a nav that borrows the
 * pill treatment without being a tablist (`blocks/NavTabs`) takes the same base rather than a copy.
 */
const TAB_TRIGGER_BASE =
  "inline-flex items-center justify-center gap-1.5 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

/** The names these two carried before the DS renamed them. */
const TABS_LEGACY_VARIANT = { pill: 'toggle', boxed: 'pills' } as const;

type TabsVariant = keyof typeof TAB_VARIANTS;
type TabsVariantInput = TabsVariant | keyof typeof TABS_LEGACY_VARIANT;

function resolveVariant(variant: TabsVariantInput): TabsVariant {
  return variant in TABS_LEGACY_VARIANT
    ? TABS_LEGACY_VARIANT[variant as keyof typeof TABS_LEGACY_VARIANT]
    : (variant as TabsVariant);
}

const TabsVariantContext = createContext<TabsVariant>('toggle');

function Tabs({ className, variant = 'toggle', ...props }: TabsPrimitive.Root.Props & { variant?: TabsVariantInput }) {
  const resolved = resolveVariant(variant);
  return (
    <TabsVariantContext.Provider value={TAB_VARIANTS[resolved] ? resolved : 'toggle'}>
      <TabsPrimitive.Root
        data-slot="tabs"
        // `pills` lays its list out against the container, not the viewport, so the tabs stack based
        // on the space the Tabs root actually has rather than on window width.
        className={cn('flex flex-col gap-y-2', resolved === 'pills' && '@container', className)}
        {...props}
      />
    </TabsVariantContext.Provider>
  );
}

function TabsList({ className, ...props }: TabsPrimitive.List.Props) {
  const variant = useContext(TabsVariantContext);
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(TAB_VARIANTS[variant].list, className)}
      {...props}
    />
  );
}

function TabsTrigger({ className, ...props }: TabsPrimitive.Tab.Props) {
  const variant = useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(TAB_TRIGGER_BASE, TAB_VARIANTS[variant].trigger, className)}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel data-slot="tabs-content" className={cn('flex-1 text-sm outline-none', className)} {...props} />
  );
}

export { TAB_TRIGGER_BASE, TAB_VARIANTS, Tabs, TabsContent, TabsList, TabsTrigger };
